import React, { useState, useEffect, useReducer, use } from 'react';
import clsx from 'clsx';
import { IProps, ISearchApiResponse } from './SearchResultsBlock.interface';
import './SearchResultsBlock.styles.css';
import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
  ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import Heading from '@/elements/Heading/Heading.component';
import Text from '@/elements/Text/Text.component';
import Button from '@/elements/Button/Button.component';
import Icon from '@/elements/Icon/Icon.component';
import AutoSuggestInput from '@/components/AutoSuggest/AutoSuggest.component';
import { isLocal } from '@/constants/local.constant';
import { dataLayerPush } from '@/utils/gtm';

// Utility functions for URL parameter handling
const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  return params;
};

const updateUrlParams = (
  params: Record<string, string | number | undefined>,
) => {
  const urlParams = new URLSearchParams(window.location.search);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      urlParams.set(key, String(value));
    } else {
      urlParams.delete(key);
    }
  });

  const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
  window.history.replaceState({}, '', newUrl);
};

function useFetchResult(
  apiUrl: string,
  queryString: string,
  page: number,
  pageSize: number,
  pageId: number | undefined,
) {
  const [status, setStatus] = useState<string>('idle');
  const [results, setResults] = useState<ISearchApiResponse>({
    query: '',
    filters: undefined,
    hits: [],
    totalResults: 0,
    totalPage: 0,
    gtmDataLayerObject: undefined,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!queryString || !apiUrl) {
        // Added apiUrl check
        setStatus('initial');
        return;
      }
      try {
        setStatus('loading');

        const response = await fetch(
          `${isLocal}${apiUrl}${queryString}&page=${page}&pagesize=${pageSize}&includeFilter=true&pageid=${pageId}`,
        );
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        const data: ISearchApiResponse = await response.json();
        dataLayerPush(data.gtmDataLayerObject);

        if (data.hits && data.hits?.length > 0) {
            setStatus('success');
            setResults({ ...data });
        } else {
          // Different handling based on page number
          if (page === 1) {
            setStatus('no-result');
            setResults({ ...data });
          } else {
            // For pagination with no more content - keep existing items
            setStatus('no-more-content');
            setResults((prev: any) => ({
              ...prev,
              hasNextPage: false,
            }));
          }
        }
      } catch (error) {
        setStatus('error');
        setResults((prev) => ({
          ...prev,
        }));
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [apiUrl, queryString, page]); // Add 'page' back as dependency

  return {
    status,
    results,
  };
}

// Add the reducer above your component
type SearchState = {
  page: number;
  category?: string;
  sort?: string;
  searchKey: string;
};

type SearchAction =
  | { type: 'SET_OPEN'; payload: boolean }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'PREVIOUS_PAGE' }
  | { type: 'NEXT_PAGE' }
  | { type: 'SET_CATEGORY'; payload: string }
  | { type: 'SET_SORT'; payload: string }
  | { type: 'SET_SEARCH_KEY'; payload: string }
  | { type: 'SET_SEARCH_KEY_FROM_URL'; payload: string } // New action for URL-based changes
  | { type: 'SET_DEBOUNCED_SEARCH_KEY'; payload: string };

function searchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'PREVIOUS_PAGE':
      return { ...state, page: state.page - 1 };
    case 'NEXT_PAGE':
      return { ...state, page: state.page + 1 };
    case 'SET_CATEGORY':
      return {
        ...state,
        category: action.payload,
        page: 1, // Reset page when category changes
      };
    case 'SET_SORT':
      return { ...state, sort: action.payload, page: 1 }; // Reset page when sort changes
    case 'SET_SEARCH_KEY':
      return { ...state, searchKey: action.payload, page: 1 }; // Reset page when search key changes
    case 'SET_SEARCH_KEY_FROM_URL':
      return { ...state, searchKey: action.payload }; // Don't reset page for URL-based changes
    default:
      return state;
  }
}

type PaginationPage = number | 'start-ellipsis' | 'end-ellipsis';

interface GetPaginationPages {
  (current: number, total: number): PaginationPage[];
}

export const SearchResultsBlock: React.FC<IProps> = ({
  className,
  autoCompleteApiUrl,
  autoCompleteRedirectUrl,
  autoCompletePlaceholder,
  autoCompleteButtonText,
  apiUrl,
  qryParamSearchQuery,
  qryParamCategories,
  qryParamCurrentPage,
  qryParamPageSize,
  qryParamSortBy,
  pageSize,
  searchQuery,
  categoryIDs,
  currentPage,
  noResultsMessage,
  errorMessage,
  searchTitle,
  mobileSearchTitle,
  searchButtonText,
  sortByOptions,
  pageId,
  ...blockWrapperProps
}) => {
  // If errorMessage is present, show it and bypass API call/rendering
  if (errorMessage) {
    return (
      <BlockWrapper className={clsx('searchResultsBlock', className)} {...blockWrapperProps}>
        <ContentWrapper className="searchResultsBlock__loading border">
          <ContentColumn className="searchResultsBlock__loadingColumn justify-center flex items-center">
            <Text className="self-center">{errorMessage}</Text>
          </ContentColumn>
        </ContentWrapper>
      </BlockWrapper>
    );
  }

  // Initialize state from URL parameters if available, otherwise use props
  const initializeStateFromUrl = () => {
    const urlParams = getUrlParams();
    const urlSearchQuery = urlParams.get(qryParamSearchQuery);
    const urlPage = urlParams.get(qryParamCurrentPage);

    return {
      // If there's a search query in URL but no page param, start from page 1
      page:
        urlSearchQuery && !urlPage
          ? 1
          : parseInt(urlPage || String(currentPage || 1)),
      category: urlParams.get(qryParamCategories) || undefined,
      sort:
        urlParams.get(qryParamSortBy) ||
        sortByOptions?.items?.find((item) => item.selected)?.value,
      searchKey: urlSearchQuery || searchQuery || '',
    };
  };

  const [state, dispatch] = useReducer(searchReducer, initializeStateFromUrl());
  const { page, sort = undefined, category = undefined, searchKey } = state;
  let queryString = `?${qryParamSearchQuery}=${encodeURIComponent(searchKey || '')}`;
  if (category) {
    queryString += `&${qryParamCategories}=${encodeURIComponent(category)}`;
  }
  if (sort) {
    queryString += `&${qryParamSortBy}=${encodeURIComponent(sort)}`;
  }

  const { results, status } = useFetchResult(
    apiUrl,
    queryString,
    page,
    pageSize,
    pageId,
  );

  // Sync URL params with state
  useEffect(() => {
    const params: Record<string, string | number | undefined> = {};

    // Always include search query (even if empty for consistency)
    params[qryParamSearchQuery] = searchKey || '';
    if (category) params[qryParamCategories] = category;
    if (sort) params[qryParamSortBy] = sort;
    // Always include page parameter for consistency
    params[qryParamCurrentPage] = page;
    if (pageSize) params[qryParamPageSize] = pageSize;

    updateUrlParams(params);
  }, [
    searchKey,
    category,
    sort,
    page,
    pageSize,
    qryParamSearchQuery,
    qryParamCategories,
    qryParamSortBy,
    qryParamCurrentPage,
    qryParamPageSize,
  ]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = getUrlParams();
      const newState = {
        page: parseInt(
          urlParams.get(qryParamCurrentPage) || String(currentPage || 1),
        ),
        category: urlParams.get(qryParamCategories) || undefined,
        sort:
          urlParams.get(qryParamSortBy) ||
          sortByOptions.items.find((item) => item.selected)?.value,
        searchKey: urlParams.get(qryParamSearchQuery) || searchQuery || '',
      }; // Update state to match URL - use SET_PAGE to avoid resetting page when navigating back
      if (newState.searchKey !== searchKey) {
        dispatch({
          type: 'SET_SEARCH_KEY_FROM_URL',
          payload: newState.searchKey,
        });
      }
      if (newState.category !== category) {
        dispatch({ type: 'SET_CATEGORY', payload: newState.category || '' });
      }
      if (newState.sort !== sort) {
        dispatch({ type: 'SET_SORT', payload: newState.sort || '' });
      }
      if (newState.page !== page) {
        dispatch({ type: 'SET_PAGE', payload: newState.page });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [
    searchKey,
    category,
    sort,
    page,
    qryParamCurrentPage,
    qryParamCategories,
    qryParamSortBy,
    qryParamSearchQuery,
    currentPage,
    searchQuery,
    sortByOptions,
  ]);

  const getPaginationPages: GetPaginationPages = (current, total) => {
    const pages: PaginationPage[] = [1];

    if (current > 3) pages.push('start-ellipsis');
    if (current > 2) pages.push(current - 1);
    if (current !== 1 && current !== total) pages.push(current);
    if (current < total - 1) pages.push(current + 1);
    if (current < total - 2) pages.push('end-ellipsis');
    if (total > 1) pages.push(total);

    return pages.filter((p, i) => pages.indexOf(p) === i);
  };

  const total = results.totalPage ?? 0;
  const pages = total > 1 ? getPaginationPages(page, total) : [];

  return (
    <BlockWrapper
      className={clsx('searchResultsBlock', className)}
      {...blockWrapperProps}
    >
      <ContentWrapper className="searchResultsBlock__search">
        <ContentColumn className="searchResultsBlock__searchColumn">
          {searchKey && (
            <>
              <Heading
                level={2}
                className="searchResultsBlock__searchTerm__mobile"
              >
                Search results for
              </Heading>
              <Heading level={2} className="searchResultsBlock__searchTerm">
                {results.totalResults} results found for
              </Heading>
            </>
          )}
          <fieldset disabled={status === 'loading'}>
            {' '}
            <AutoSuggestInput
              key={searchKey} // Force re-render when searchKey changes
              name="search"
              label="Search"
              placeholder={autoCompletePlaceholder || 'Search...'}
              apiUrl={autoCompleteApiUrl || ''}
              onSearchSubmit={(newValue) => {
                if (!newValue?.trim()) {
                  return; // Prevent empty searches
                }
                dispatch({ type: 'SET_SEARCH_KEY', payload: newValue });
              }}
              onSuggestionSelect={(newValue) => {
                if (!newValue?.trim()) {
                  return; // Prevent empty searches
                }
                dispatch({ type: 'SET_SEARCH_KEY', payload: newValue });
              }}
              initialValue={searchKey || ''}
              buttonLabel={autoCompleteButtonText || 'Search'}
              buttonLocation="internal"
              viewAllCta={{
                children: 'See all results',
                className: 'searchResultsBlock__viewAllCta',
                variant: 'primary',
                color: 'primary',
                size: 'lg',
                onClick: (e, currentQuery) => {
                  e.preventDefault();
                  if (currentQuery) {
                    dispatch({ type: 'SET_SEARCH_KEY', payload: currentQuery });
                  }
                },
              }}
            />
          </fieldset>
        </ContentColumn>
      </ContentWrapper>
      {status === 'loading' && (
        <ContentWrapper className="searchResultsBlock__loading border">
          <ContentColumn className="searchResultsBlock__loadingColumn justify-center flex items-center">
            <Text className="self-center">Loading Search Results...</Text>
          </ContentColumn>
        </ContentWrapper>
      )}

      {status === 'success' && (
        <ContentWrapper className="searchResultsBlock__content">
          <ContentColumn className="searchResultsBlock__sidebar">
            {
              (results?.filters?.contentTypeCategories?.items?.length ?? 0) > 0 && (
                <div className="searchResultsBlock__filters">
                  <ul className="searchResultsBlock__filterList">
                    {results.filters?.contentTypeCategories?.items?.map(
                      (item) => (
                        <li
                          key={item.value}
                          className="searchResultsBlock__filterItem"
                        >
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              value={item.value}
                              checked={item.selected}
                              onChange={(e) => {
                                // Split current category string into array, or start with []
                                const currentCategories = category
                                  ? category.split(',')
                                  : [];
                                let newCategories;
                                if (item.selected) {
                                  // Unchecking: remove this value
                                  newCategories = currentCategories.filter(
                                    (val) => val !== item.value,
                                  );
                                } else {
                                  // Checking: add this value
                                  newCategories = [
                                    ...currentCategories,
                                    item.value,
                                  ];
                                }
                                dispatch({
                                  type: 'SET_CATEGORY',
                                  payload: newCategories.join(','),
                                });
                              }}
                              aria-checked={item.selected}
                            />
                            <span>{item.text}</span>
                          </label>
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )
            }
          </ContentColumn>
          <ContentColumn className="searchResultsBlock__main">
            <div className="searchResultsBlock__controls">
              <div className="searchResultsBlock__sort flex flex-col w-full">
                <div className="flex flex-row items-center gap-4 w-full">
                  <label
                    className="searchResultsBlock__sortLabel"
                    htmlFor="sortOrderRadios"
                  >
                    Sort by
                  </label>
                  <div
                    className="flex flex-row gap-6 items-center"
                    id="sortOrderRadios"
                  >
                    {sortByOptions.items.map((item) => (
                      <label
                        key={item.value}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="sortOrder"
                          value={item.value}
                          checked={sort === item.value}
                          onChange={() =>
                            dispatch({ type: 'SET_SORT', payload: item.value })
                          }
                          className="searchResultsBlock__sortRadio"
                        />
                        <span
                          className={
                            sort === item.value
                              ? 'searchResultsBlock__sortText searchResultsBlock__sortText--selected'
                              : 'searchResultsBlock__sortText'
                          }
                        >
                          {item.text}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <hr className="mt-4 border-t border-neutral-300 w-full" />
              </div>
            </div>
            {results.hits && results.hits.length > 0 ? (
              <ul className="searchResultsBlock__resultsList">
                {results.hits.map((item, idx) => (
                  <li key={idx} className="searchResultsBlock__resultItem">
                    <div>
                      <div className="searchResultsBlock__heading">
                        {item.featured && (
                          <Text className="searchResultsBlock__featuredTag">
                            Featured
                          </Text>
                        )}
                        {item.fileType && (
                          <Text className="searchResultsBlock__fileTypeTag">
                            <Icon icon="download" /> File
                          </Text>
                        )}
                        <a
                          href={item.href}
                          target={item.hrefTarget || '_self'}
                          className="searchResultsBlock__resultLink"
                        >
                          <Heading
                            level={3}
                            html={item.title}
                            className="searchResultsBlock__resultTitle"
                          />
                        </a>
                      </div>

                      <Text
                        html={item.summary}
                        className="searchResultsBlock__resultSummary"
                      />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <Text className="mt-[500px]">No results found.</Text>
            )}
            {pages.length > 1 && (
              <div className="searchResultsBlock__pagination">
                {/* Previous */}
                <div className="searchResultsBlock__paginationLeft">
                  {page > 1 ? (
                    <Button
                      onClick={() => dispatch({ type: 'PREVIOUS_PAGE' })}
                      variant="tertiary"
                      className="searchResultsBlock__prevNext"
                      aria-label="Go to previous page"
                      icon="arrow-left"
                      iconPosition="before"
                    >
                      <span className="searchResultsBlock__prevNext__text">
                        Previous
                      </span>
                    </Button>
                  ) : (
                    <div className="searchResultsBlock__prevNextPlaceholder" />
                  )}
                </div>

                {/* Page Numbers */}
                <div className="searchResultsBlock__paginationCenter">
                  {pages.map((p, idx) =>
                    typeof p === 'string' ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="searchResultsBlock__ellipsis"
                        aria-hidden="true"
                      >
                        …
                      </span>
                    ) : (
                      <Button
                        key={p}
                        onClick={() =>
                          dispatch({ type: 'SET_PAGE', payload: p })
                        }
                        variant="tertiary"
                        className={clsx('searchResultsBlock__pageNumber', {
                          'searchResultsBlock__pageNumber--current': page === p,
                        })}
                        aria-current={page === p ? 'page' : undefined}
                        aria-label={
                          page === p
                            ? `Current page, page ${p}`
                            : `Go to page ${p}`
                        }
                      >
                        {p}
                      </Button>
                    ),
                  )}
                </div>

                {/* Next */}
                <div className="searchResultsBlock__paginationRight">
                  {page < total ? (
                    <Button
                      onClick={() => dispatch({ type: 'NEXT_PAGE' })}
                      variant="tertiary"
                      className="searchResultsBlock__prevNext"
                      aria-label="Go to next page"
                      icon="arrow-right"
                      iconPosition="after"
                    >
                      <span className="searchResultsBlock__prevNext__text">
                        Next
                      </span>
                    </Button>
                  ) : (
                    <div className="searchResultsBlock__prevNextPlaceholder" />
                  )}
                </div>
              </div>
            )}
          </ContentColumn>
        </ContentWrapper>
      )}

      {status === 'no-result' && (
        <ContentWrapper className="searchResultsBlock__loading border">
          <ContentColumn className="searchResultsBlock__loadingColumn justify-center flex items-center">
            <Text className="self-center">
              {noResultsMessage || 'No results found'}
            </Text>
          </ContentColumn>
        </ContentWrapper>
      )}
    </BlockWrapper>
  );
};
